import React, { useEffect, useMemo, useState } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { useTitle } from "../../../../../hooks/useTitle";
import { withAuth } from "../../../../../components/HOC/withAuth";
import CardContainer from "../../../../../components/CustomUI/CardContainer";
import Button from "../../../../../components/BaseUI/Button";
import Input from "../../../../../components/BaseUI/Input";
import { SectionSpinner, SectionEmpty } from "../../shared/utils";
import ProductionReportShell from "../ProductionReportShell";
import { usePartProductionReport } from "../usePartProductionReport";
import "../../Reports.scss";

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString("tr-TR");
const COLS = ["Parça", "Makine", "Üretim"];

function buildMatrixGroups(rows) {
  const map = new Map();

  for (const row of rows) {
    const gid = String(row.groupPartId || row.mainPartId || row.partId);
    if (!map.has(gid)) {
      map.set(gid, {
        id: gid,
        name: row.mainPartName || row.partName,
        code: row.mainPartCode || row.partCode,
        totalQty: 0,
        machines: new Set(),
        parts: new Set(),
        hasVariants: false,
        rows: [],
      });
    }
    const g = map.get(gid);
    g.totalQty += row.qty;
    g.machines.add(String(row.machineId));
    g.parts.add(String(row.partId));
    if (row.mainPartId) g.hasVariants = true;
    g.rows.push(row);
  }

  return [...map.values()]
    .map((g) => ({
      ...g,
      machineCount: g.machines.size,
      partCount: g.parts.size,
      rows: g.rows.sort((a, b) => b.qty - a.qty),
    }))
    .sort((a, b) => b.totalQty - a.totalQty);
}

function rowSearchText(row) {
  return [
    row.machineName,
    row.machineCode,
    row.partName,
    row.partCode,
    row.variantLabel,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function applySearchFilter(groups, q) {
  if (!q) {
    return groups.map((g) => ({
      ...g,
      visibleRows: g.rows,
      displayQty: g.totalQty,
    }));
  }

  return groups
    .map((g) => {
      const mainMatches =
        g.name?.toLowerCase().includes(q) || g.code?.toLowerCase().includes(q);
      const visibleRows = mainMatches
        ? g.rows
        : g.rows.filter((r) => rowSearchText(r).includes(q));
      if (!visibleRows.length) return null;
      return {
        ...g,
        visibleRows,
        displayQty: visibleRows.reduce((s, r) => s + r.qty, 0),
      };
    })
    .filter(Boolean);
}

const ProductionByPartMatrix = () => {
  useTitle("Rovion MES - Üretim Matrisi");
  const {
    filters,
    machines,
    shifts,
    loading,
    derived,
    fetchData,
    toasts,
    dismissToast,
  } = usePartProductionReport();

  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");

  const groups = useMemo(
    () =>
      derived?.machineBreakdown
        ? buildMatrixGroups(derived.machineBreakdown)
        : [],
    [derived],
  );

  useEffect(() => {
    if (!groups.length) return;
    setExpanded((prev) => {
      if (Object.keys(prev).length) return prev;
      return Object.fromEntries(groups.map((g) => [g.id, true]));
    });
  }, [groups]);

  const searchQuery = search.trim().toLowerCase();

  const filteredGroups = useMemo(
    () => applySearchFilter(groups, searchQuery),
    [groups, searchQuery],
  );

  useEffect(() => {
    if (!searchQuery || !filteredGroups.length) return;
    setExpanded((prev) => {
      const next = { ...prev };
      filteredGroups.forEach((g) => {
        next[g.id] = true;
      });
      return next;
    });
  }, [searchQuery, filteredGroups]);

  const stats = useMemo(() => {
    const detailRows = filteredGroups.reduce(
      (n, g) => n + g.visibleRows.length,
      0,
    );
    const totalQty = filteredGroups.reduce((n, g) => n + g.displayQty, 0);
    return { detailRows, totalQty };
  }, [filteredGroups]);

  const expandAll = () =>
    setExpanded(Object.fromEntries(groups.map((g) => [g.id, true])));
  const collapseAll = () =>
    setExpanded(Object.fromEntries(groups.map((g) => [g.id, false])));
  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const content = () => {
    if (loading) return <SectionSpinner />;
    if (!derived) return <SectionEmpty />;
    if (!groups.length) return <SectionEmpty msg="Matris verisi bulunamadı." />;

    return (
      <CardContainer title="Makine × Parça Üretim Matrisi">
        <div className="rp-matrix-toolbar">
          <Input
            placeholder="Ara..."
            value={search}
            onChange={(v) => setSearch(v ?? "")}
            style={{ maxWidth: 400, marginLeft: "25px" }}
          />
          <div className="rp-matrix-toolbar-actions">
            <Button appearance="default" onClick={expandAll}>
              Tümünü aç
            </Button>
            <Button appearance="default" onClick={collapseAll}>
              Tümünü kapat
            </Button>
            <span className="rp-matrix-stats">
              {filteredGroups.length} ana parça · {stats.detailRows} satır
            </span>
          </div>
        </div>

        {!filteredGroups.length ? (
          <SectionEmpty msg="Arama sonucu bulunamadı." />
        ) : (
          <div className="rp-matrix-breakdown-wrap">
            <table className="rp-matrix-breakdown">
              <thead>
                <tr>
                  {COLS.map((h, i) => (
                    <th
                      key={h}
                      className={`${i === 2 ? "text-end" : ""} ${i === 0 ? "text-left" : ""}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((group) => {
                  const isOpen = !!expanded[group.id];
                  return (
                    <React.Fragment key={group.id}>
                      <tr
                        className={`rp-matrix-group-row is-clickable ${isOpen ? "is-open" : ""}`}
                        onClick={() => toggle(group.id)}
                      >
                        <td>
                          <div className="rp-matrix-group-label">
                            <span className="rp-matrix-chevron">
                              {isOpen ? (
                                <FiChevronDown size={16} />
                              ) : (
                                <FiChevronRight size={16} />
                              )}
                            </span>
                            <div>
                              <strong>{group.name}</strong>{" "}
                              <span className="rp-matrix-muted">
                                {group.code}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="rp-matrix-scope">
                            {searchQuery
                              ? `${group.visibleRows.length} eşleşme`
                              : `${group.machineCount} makine${
                                  group.hasVariants
                                    ? ` · ${group.partCount} varyant`
                                    : ""
                                }`}
                          </span>
                        </td>
                        <td className="text-end">
                          <strong>{fmt(group.displayQty)}</strong>
                        </td>
                      </tr>
                      {isOpen &&
                        group.visibleRows.map((row, i) => (
                          <tr
                            key={`${group.id}-${i}`}
                            className="rp-matrix-child-row"
                          >
                            <td>
                              <div className="rp-matrix-child-variant">
                                <span className="rp-matrix-child-indent" />
                                <span>{row.partName}</span>
                              </div>
                            </td>
                            <td>
                              <strong>{row.machineName}</strong>{" "}
                              <span className="rp-matrix-muted">
                                {row.machineCode}
                              </span>
                            </td>
                            <td className="text-end">{fmt(row.qty)}</td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="rp-matrix-total-row">
                  <td colSpan={2}>
                    <span className="rp-matrix-total-label">TOPLAM</span>
                  </td>
                  <td className="text-end">{fmt(stats.totalQty)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContainer>
    );
  };

  return (
    <ProductionReportShell
      title="Üretim Matrisi"
      filters={filters}
      machines={machines}
      shifts={shifts}
      onApply={fetchData}
      toasts={toasts}
      dismissToast={dismissToast}
    >
      {content()}
    </ProductionReportShell>
  );
};

export default withAuth(ProductionByPartMatrix);
